import { apiGet, apiSend, apiUpload } from "./api";
import { Program, ProgramFormValues, ProgramImage, ProgramListItem } from "./types";
import { invalidateProgramsCache } from "../api/programsCache";

export async function listPrograms(): Promise<ProgramListItem[]> {
  const data = await apiGet("/admin/programs");
  return data.programs as ProgramListItem[];
}

// The "显示完整信息" toggle's on-demand fetch — every program's full
// brochure content (hero copy, advantages, requirements, fee tables, process
// steps, etc.), not just the lean listPrograms() shape.
export async function listProgramsFull(): Promise<Program[]> {
  const data = await apiGet("/admin/programs?full=true");
  return data.programs as Program[];
}

export async function getProgram(id: string): Promise<Program> {
  const data = await apiGet(`/admin/programs/id/${id}`);
  return data.program as Program;
}

export async function createProgram(
  slug: string,
  values: ProgramFormValues,
): Promise<Program> {
  const data = await apiSend("POST", "/admin/programs", { slug, ...values });
  // A newly-created program (or one just flipped to published) otherwise
  // wouldn't reach the public site's homepage/header nav for up to the
  // cache's 5-minute TTL -- see programsCache.ts's invalidateProgramsCache.
  invalidateProgramsCache();
  return data.program as Program;
}

export async function updateProgram(
  id: string,
  values: Partial<ProgramFormValues> & { slug?: string },
): Promise<Program> {
  const data = await apiSend("PUT", `/admin/programs/${id}`, values);
  invalidateProgramsCache();
  return data.program as Program;
}

export async function deleteProgram(id: string): Promise<void> {
  await apiSend("DELETE", `/admin/programs/${id}`);
  invalidateProgramsCache();
}

export async function uploadProgramImage(
  programId: string,
  file: File,
  role: ProgramImage["role"],
  caption?: string,
): Promise<ProgramImage> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("role", role);
  if (caption) formData.append("caption", caption);
  const data = await apiUpload(`/admin/programs/${programId}/images/upload`, formData);
  // Covers the flag icon shown next to the program in the header nav and
  // homepage grid, which is read off this same cached list.
  invalidateProgramsCache();
  return data.image as ProgramImage;
}

export async function deleteProgramImage(programId: string, imageId: string): Promise<void> {
  await apiSend("DELETE", `/admin/programs/${programId}/images/${imageId}`);
  invalidateProgramsCache();
}

// Unlike the public PDF endpoint, works regardless of draft/published status
// — used by the admin table's per-row PDF button (see program.routes.ts).
export async function getAdminProgramPdfUrl(id: string): Promise<string> {
  const data = await apiGet(`/admin/programs/${id}/pdf`);
  return data.url as string;
}

// The admin table's "更新 PDF" button — forces a fresh render and overwrites
// whatever's cached in S3, even if the program's content hasn't changed
// since the last render (see pdf.service.ts's regenerateProgramPdf).
export async function regenerateProgramPdf(id: string): Promise<string> {
  const data = await apiSend("POST", `/admin/programs/${id}/pdf/regenerate`);
  return data.url as string;
}
